<template>
  <section class="poll" v-if="poll">
    <h3 class="poll-title">{{ poll.question || 'Quick poll' }}</h3>
    <div class="options">
      <button
        v-for="opt in poll.options"
        :key="opt.id"
        type="button"
        class="btn option"
        :class="{
          selected: isSelected(opt.id),
          locked: locked,
          'btn-secondary': !isSelected(opt.id),
          'btn-primary': isSelected(opt.id),
        }"
        :disabled="locked || submitting"
        :aria-pressed="isSelected(opt.id)"
        @click="allowMultiple ? toggle(opt.id) : pick(opt.id)"
      >
        <span class="label">{{ opt.label }}</span>
        <span v-if="isSelected(opt.id)" class="badge">{{ locked ? 'Your pick' : 'Selected' }}</span>
      </button>
    </div>
    <!-- Picks stay changeable until submit, including after leaving this slide. -->
    <div v-if="!locked" class="multi-actions">
      <span class="hint">{{ allowMultiple ? 'Pick all that apply. You can change these until you submit.' : 'Pick one. Click it again to undo, or choose another, until you submit.' }}</span>
      <button
        type="button"
        class="btn btn-primary submit"
        :disabled="submitting || !hasPick"
        @click="allowMultiple ? submitMulti() : submitSingle()"
      >
        {{ submitting ? 'Submitting…' : (allowMultiple ? submitLabel : 'Submit answer') }}
      </button>
    </div>
    <p v-if="locked" class="done">Thanks — your {{ allowMultiple ? 'picks are' : 'answer is' }} in.</p>
    <p v-if="error" class="error">{{ error }}</p>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { vote, getMyVote } from '../services/api'

const props = defineProps({
  slideId: { type: String, required: true },
  poll: { type: Object, required: true },
})

const allowMultiple = computed(() => !!props.poll?.allowMultiple)
const selectedId = ref(null) // single-select pick, changeable until submit
const selectedIds = ref([]) // multi-select picks, changeable until submit
const committed = ref(false) // locked only after a vote is saved
const submitting = ref(false)
const error = ref('')

// A poll locks when the vote is on the server. Until then the student can
// undo a pick or switch, and that draft survives leaving the slide.
const locked = computed(() => committed.value)
const hasPick = computed(() =>
  allowMultiple.value ? selectedIds.value.length > 0 : !!selectedId.value,
)
const submitLabel = computed(() => {
  const n = selectedIds.value.length
  return `Submit ${n || ''} pick${n === 1 ? '' : 's'}`.replace('  ', ' ').trim()
})

function draftKey() {
  return `u80-poll-draft:${props.slideId}`
}

function knownOption(id) {
  return !!props.poll?.options?.some((opt) => opt.id === id)
}

function persistDraft() {
  if (committed.value) return
  try {
    if (allowMultiple.value) {
      if (!selectedIds.value.length) sessionStorage.removeItem(draftKey())
      else sessionStorage.setItem(draftKey(), JSON.stringify({ choiceIds: [...selectedIds.value] }))
    } else if (!selectedId.value) {
      sessionStorage.removeItem(draftKey())
    } else {
      sessionStorage.setItem(draftKey(), JSON.stringify({ choiceId: selectedId.value }))
    }
  } catch {
    /* private mode or a full store: the pick still works on this page */
  }
}

function clearDraft() {
  try {
    sessionStorage.removeItem(draftKey())
  } catch {
    /* ignore */
  }
}

function restoreDraft() {
  try {
    const raw = sessionStorage.getItem(draftKey())
    if (!raw) return
    const data = JSON.parse(raw)
    if (allowMultiple.value && Array.isArray(data.choiceIds)) {
      selectedIds.value = data.choiceIds.filter(knownOption)
    } else if (!allowMultiple.value && knownOption(data.choiceId)) {
      selectedId.value = data.choiceId
    }
  } catch {
    /* a bad draft should not block voting */
  }
}

function isSelected(id) {
  return allowMultiple.value
    ? selectedIds.value.includes(id)
    : selectedId.value === id
}

function applyVote(v) {
  if (Array.isArray(v.choiceIds) && v.choiceIds.length) {
    if (allowMultiple.value) selectedIds.value = [...v.choiceIds]
    else selectedId.value = v.choiceIds[0]
    committed.value = true
    return
  }
  if (v.choiceId) {
    if (allowMultiple.value) selectedIds.value = [v.choiceId]
    else selectedId.value = v.choiceId
    committed.value = true
  }
}

async function load() {
  error.value = ''
  selectedId.value = null
  selectedIds.value = []
  committed.value = false
  try {
    const res = await getMyVote(props.slideId)
    if (res?.vote) {
      applyVote(res.vote)
      if (committed.value) {
        clearDraft()
        return
      }
    }
  } catch {
    /* keep going so an unsubmitted draft can still come back */
  }
  restoreDraft()
}

function pick(id) {
  if (locked.value || submitting.value) return
  selectedId.value = selectedId.value === id ? null : id
  persistDraft()
}

function toggle(id) {
  if (locked.value || submitting.value) return
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter((x) => x !== id)
  } else {
    selectedIds.value = [...selectedIds.value, id]
  }
  persistDraft()
}

async function submitSingle() {
  if (locked.value || submitting.value || !selectedId.value) return
  submitting.value = true
  error.value = ''
  const choiceId = selectedId.value
  try {
    await vote(props.slideId, choiceId)
    committed.value = true
    clearDraft()
  } catch (e) {
    if (e.status === 409) {
      committed.value = true
      await load()
    } else {
      error.value = e.message || 'Vote failed'
    }
  } finally {
    submitting.value = false
  }
}

async function submitMulti() {
  if (locked.value || submitting.value || !selectedIds.value.length) return
  submitting.value = true
  error.value = ''
  try {
    await vote(props.slideId, [...selectedIds.value])
    committed.value = true
    clearDraft()
  } catch (e) {
    if (e.status === 409) {
      committed.value = true
      await load()
    } else {
      error.value = e.message || 'Vote failed'
    }
  } finally {
    submitting.value = false
  }
}

function onCleared(e) {
  if (!e.detail?.slideId || e.detail.slideId === props.slideId) load()
}

onMounted(() => {
  load()
  window.addEventListener('u80-poll-cleared', onCleared)
})
onUnmounted(() => {
  window.removeEventListener('u80-poll-cleared', onCleared)
})
watch(() => props.slideId, load)
</script>

<style scoped>
.poll {
  margin-top: 0;
  padding: 1.25rem 1.35rem;
  background: var(--paper-warm);
  border: 1px solid var(--border);
  border-radius: 8px;
}

.poll-title {
  margin: 0 0 0.85rem;
  font-size: 1.15rem;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option {
  text-align: left;
  width: 100%;
  justify-content: space-between;
  gap: 0.75rem;
}

.option.locked:not(.selected) {
  opacity: 0.55;
  cursor: default;
}

.option.selected:not(:disabled) {
  cursor: pointer;
}

.option:disabled {
  cursor: default;
}

.badge {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.95;
}

.error {
  color: var(--danger);
  margin: 0.75rem 0 0;
}

.multi-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.85rem;
}

.multi-actions .hint {
  font-size: 0.8rem;
  color: var(--muted);
}

.multi-actions .submit {
  flex-shrink: 0;
}

.done {
  margin: 0.85rem 0 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
}
</style>
